import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Usuario } from '../types'
import { useDatos } from './datos'

/** NovaBerry es una app interna: no hay cuentas, ni por persona ni de
 *  equipo. El link ya limita quién puede llegar hasta acá, así que el
 *  único paso al abrir la app es decir quién eres, tocando tu nombre en
 *  una lista — no es una barrera de seguridad, es solo para que las
 *  ventas y la bitácora queden atribuidas a quien corresponde. */

/** Traduce errores técnicos de guardado a algo accionable. El más común
 *  en este punto es que la base todavía tenga reglas de seguridad (RLS)
 *  que exigen una sesión que la app ya no abre. */
function traducirErrorGuardado(error: unknown): string {
  const texto = error instanceof Error ? error.message : String(error)
  if (texto.includes('row-level security') || texto.includes('42501')) {
    return 'La base de datos todavía exige una sesión que esta app ya no usa. Hay que actualizar los permisos (RLS) en Supabase — revisa supabase/schema.sql.'
  }
  return texto || 'No se pudo guardar. Intenta de nuevo.'
}

interface EstadoSesion {
  usuario: Usuario | null
  /** Identifica a la persona que va a usar la app en este momento. */
  elegirPersona: (usuario: Usuario) => Promise<void>
  /** Crea el primer perfil de administrador cuando todavía no existe
   *  ninguno (primer arranque de la app). */
  crearPrimeraPersona: (nombre: string) => Promise<void>
  /** "Despachos" no es una persona: es una identidad compartida para
   *  quien reparte. Se crea sola la primera vez que alguien la toca. */
  entrarComoDespachos: () => Promise<void>
  /** Deja de ser "esa persona" y vuelve al selector. Sirve para cambiar
   *  de vendedor en un mismo equipo compartido. */
  salir: () => Promise<void>
  esAdmin: () => boolean
}

export const useSesion = create<EstadoSesion>()(
  persist(
    (set, get) => ({
      usuario: null,

      async elegirPersona(usuario) {
        if (!usuario.activo) throw new Error('Esta cuenta está desactivada. Habla con el administrador.')
        const conectado: Usuario = { ...usuario, ultimaConexion: new Date().toISOString() }
        const datos = useDatos.getState()
        try {
          await datos.guardarUsuario(conectado, conectado)
        } catch (e) {
          throw new Error(traducirErrorGuardado(e))
        }
        // Recién se marca la sesión como abierta cuando el guardado real
        // tuvo éxito: si falla, mejor quedarse en el selector que fingir
        // haber entrado.
        set({ usuario: conectado })
        await datos.anotar(conectado, 'inicio_sesion', 'sesión', 'Inició sesión')
      },

      async crearPrimeraPersona(nombre) {
        const datos = useDatos.getState()
        const { uid } = await import('../lib/cn')
        const admin: Usuario = {
          id: uid('u'),
          nombre: nombre.trim(),
          email: '',
          rol: 'admin',
          activo: true,
          comision: 0,
          creado: new Date().toISOString(),
        }
        try {
          await datos.guardarUsuario(admin, admin)
        } catch (e) {
          throw new Error(traducirErrorGuardado(e))
        }
        set({ usuario: admin })
        await datos.anotar(admin, 'crear', 'usuario', 'Primer perfil de administrador')
      },

      async entrarComoDespachos() {
        const datos = useDatos.getState()
        const existente = datos.usuarios.find((u) => u.rol === 'despacho')
        if (existente) {
          await get().elegirPersona(existente)
          return
        }
        const { uid } = await import('../lib/cn')
        const despachos: Usuario = {
          id: uid('u'),
          nombre: 'Despachos',
          rol: 'despacho',
          activo: true,
          comision: 0,
          creado: new Date().toISOString(),
        }
        try {
          await datos.guardarUsuario(despachos, despachos)
        } catch (e) {
          throw new Error(traducirErrorGuardado(e))
        }
        set({ usuario: despachos })
        await datos.anotar(despachos, 'crear', 'usuario', 'Perfil de Despachos')
      },

      async salir() {
        const usuario = get().usuario
        if (usuario) {
          await useDatos.getState().anotar(usuario, 'cierre_sesion', 'sesión', 'Cerró sesión')
        }
        set({ usuario: null })
      },

      esAdmin() {
        return get().usuario?.rol === 'admin'
      },
    }),
    {
      name: 'novaberry-sesion',
      // El modelo de "usuario" cambió varias veces mientras se armaba
      // esta app (tuvo contraseña, después no; correo obligatorio,
      // después opcional...). Un navegador que guardó una sesión de
      // antes de alguno de esos cambios puede tener un objeto que ya no
      // calza con lo que el código de hoy espera, y eso rompe cualquier
      // pantalla que lea `usuario.rol` o `usuario.activo`. Subir la
      // versión fuerza a descartar cualquier sesión guardada con una
      // forma vieja, en vez de intentar adivinarla.
      version: 1,
      migrate: () => ({ usuario: null }),
    },
  ),
)

/** Atajo para acciones que exigen usuario. Si no hay sesión la llamada
 *  no debería haber ocurrido, así que fallar fuerte es lo correcto. */
export function usuarioActual(): Usuario {
  const u = useSesion.getState().usuario
  if (!u) throw new Error('No hay una sesión abierta.')
  return u
}
